<script setup>
import Checkbox from '@/components/Checkbox.vue';
import GuestLayout from '@/layouts/GuestLayout.vue';
import InputError from '@/components/InputError.vue';
import InputLabel from '@/components/InputLabel.vue';
import PrimaryButton from '@/components/PrimaryButton.vue';
import TextInput from '@/components/TextInput.vue';
import {Head, Link, useForm, usePage} from '@inertiajs/vue3';
import { userLoggedIn } from "@/api/analytics.service.js";
import Google from "@/shared/ui/icons/Google.vue";
import {onMounted, ref} from "vue";
import Logo from "@/components/Logo.vue";
import {saveUtmParams} from "@/features/register/services/utm-params.service.js";

const props = defineProps({
    canResetPassword: {
        type: Boolean,
    },
    status: {
        type: String,
    },
    utmParams: {
        type: Object
    }
});

const form = useForm({
    email: '',
    password: '',
    remember: true,
});

const isLoggingViaGoogle = ref(false);

const submit = () => {
    form.post(route('login'), {
        onFinish: () => form.reset('password'),
        onSuccess: () => {
            userLoggedIn(usePage().props.auth.user);
        }
    });
};

const onLoginViaGoogle = () => {
    if (isLoggingViaGoogle.value) {
        return;
    }
    isLoggingViaGoogle.value = true;
    window.location.href = route('auth.google.redirect');
}

onMounted(() => {
    if (props.utmParams) {
        saveUtmParams(props.utmParams);
    }
})
</script>

<template>
    <GuestLayout is-centered>
        <Head title="Log in" />

        <div v-if="status" class="mb-4 font-medium text-sm text-green-600 max-w-md">
            {{ status }}
        </div>


        <div class="flex min-h-full flex-1 flex-col justify-center px-6 py-12 lg:px-8 w-full">
            <div class="sm:mx-auto sm:w-full max-w-sm">
                <img class="h-24 w-24 m-auto hidden md:block" src="/Logo.svg" alt="How2Spanish logo" />
                <h2 class="mt-10 text-center text-2xl/9 font-bold tracking-tight text-gray-900">{{ $t('login_page.sign_in') }}</h2>
            </div>

            <div class="mt-10 sm:mx-auto sm:w-full max-w-sm">
                <form @submit.prevent="submit"  class="space-y-6" action="#" method="POST">
                    <div>
                        <label for="email" class="block text-sm/6 font-medium text-gray-900">{{ $t('login_page.email') }}</label>
                        <div class="mt-2">

                            <text-input
                                id="email"
                                type="email"
                                class="mt-1 block w-full"
                                v-model.trim="form.email"
                                required
                                autofocus
                                autocomplete="username"
                            />
                        </div>
                        <InputError class="mt-2" :message="form.errors.email" />
                    </div>

                    <div>
                        <div class="flex items-center justify-between">
                            <label for="password" class="block text-sm/6 font-medium text-gray-900">{{ $t('login_page.password') }}</label>
                        </div>
                        <div class="mt-2">
                            <text-input
                                id="password"
                                type="password"
                                class="mt-1 block w-full"
                                v-model="form.password"
                                required
                                autocomplete="current-password"
                            />
                        </div>
                    </div>

                    <div class="flex items-center justify-between">
                        <label class="flex items-center">
                            <Checkbox name="remember" v-model:checked="form.remember" />
                            <span class="ms-2 text-sm text-gray-600 dark:text-gray-400">{{ $t('login_page.remember_me') }}</span>
                        </label>

                        <div class="text-sm">
                            <a v-if="canResetPassword"
                               :href="route('password.request')"
                               class="font-semibold text-blue-600 hover:text-blue-500">{{ $t('login_page.forgot_your_password') }}</a>
                        </div>
                    </div>

                    <div>
                        <primary-button class="w-full" :class="{ 'opacity-25': form.processing }" :disabled="form.processing || isLoggingViaGoogle">
                            {{ $t('login_page.login') }}
                        </primary-button>
                    </div>
                </form>

                <div class="mt-6">
                    <div class="relative">
                        <div class="absolute inset-0 flex items-center">
                            <div class="w-full border-t border-gray-300" />
                        </div>
                        <div class="relative flex justify-center text-sm">
                            <span class="px-2 bg-white text-gray-500">{{ $t('login_page.continue_with') }}</span>
                        </div>
                    </div>

                    <div class="mt-6">
                        <button
                            @click="onLoginViaGoogle"
                            class="w-full inline-flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm
                             bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 transition-colors duration-200  disabled:cursor-not-allowed "
                        >
                            <google v-if="!isLoggingViaGoogle" class="w-5 h-5" />
                            <span v-if="!isLoggingViaGoogle" class="ml-2">Google</span>
                            <svg v-if="isLoggingViaGoogle" class="animate-spin -ml-1 mr-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                        </button>
                    </div>
                </div>

                <!-- Register Link -->
                <div class="mt-6 text-center">
                    <p class="text-sm text-gray-600">
                        {{ $t('login_page.no_account') }}
                        <a :href="route('register')" class="font-semibold text-blue-600 hover:text-blue-500">
                            {{ $t('login_page.register') }}
                        </a>
                    </p>
                </div>
            </div>
        </div>
    </GuestLayout>
</template>
